import { formatDistanceToNow, isValid, parseISO } from 'date-fns';

/**
 * Indian Standard Time — the zone every time on the platform is stated in.
 *
 * <p>Pinning the zone here, rather than letting `Intl` default to the viewer's machine, means a device
 * set to the wrong timezone still shows the same time as everyone else's. IST remains the single
 * authoritative reading: when two people discuss an interview slot, they are quoting the same clock.</p>
 *
 * <p>A viewer outside India additionally gets their own time in parentheses (see
 * `viewerParenthetical`) — an <b>annotation</b> on the IST value, never a replacement for it. That is
 * deliberately not the same thing as rendering each screen in "the right" zone, which would require
 * deciding whose zone each screen belongs to — the viewer's, the job's, or the interviewer's. That is a
 * design decision, not a constant, and it is still not made here.</p>
 */
const IST_TIME_ZONE = 'Asia/Kolkata';

/** Appended to every format that carries a clock time. See `formatDateTime`. */
const IST_SUFFIX = 'IST';

/**
 * Month abbreviations, written out rather than taken from `Intl`.
 *
 * <p>No day-first English locale abbreviates September as "Sep" — `en-IN`, `en-GB` and `en-AU` all
 * render "Sept", while the backend's invariant-culture `MMM` renders "Sep". Taking these from `Intl`
 * would make that one month disagree between a notification and the page it links to. `Intl` is used
 * only for the IST shift; the text is assembled here, where a new CLDR release cannot change it.</p>
 */
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

interface IstParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/**
 * An instant's calendar and clock fields AS SEEN IN a given zone. `hourCycle: 'h23'` keeps the hour a
 * plain 0–23 number so the 12-hour conversion below is ours to control; `en-US` is an implementation
 * detail here — only the numeric part values are read, never the assembled string.
 */
function zoneParts(date: Date, timeZone: string): IstParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const value = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    hour: value('hour'),
    minute: value('minute'),
  };
}

const istParts = (date: Date): IstParts => zoneParts(date, IST_TIME_ZONE);

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "6:45 PM" — 12-hour clock with an uppercase, undotted meridiem, matching C#'s `h:mm tt`. */
function clock({ hour, minute }: IstParts): string {
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${pad2(minute)} ${hour < 12 ? 'AM' : 'PM'}`;
}

/** "14 Jul 2026" — matching C#'s `dd MMM yyyy`. */
function calendar({ year, month, day }: IstParts): string {
  return `${pad2(day)} ${MONTHS[month - 1]} ${year}`;
}

/** Parses a backend `DateTimeOffset` ISO string; returns null for invalid/empty input. */
function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = parseISO(value);
  return isValid(date) ? date : null;
}

/**
 * Overrides the viewer's zone for one call, instead of using the one the app declared at startup. Tests
 * use it to assert the conversion against a named zone rather than whichever one the host reports.
 */
export interface ViewerZoneOptions {
  /** IANA zone to read the viewer's local time in. Defaults to the zone `setViewerTimeZone` declared. */
  viewerTimeZone?: string;
}

/**
 * The viewer's zone. IST until the app declares otherwise, so the annotation is ABSENT by default
 * rather than derived from whatever zone the host happens to report.
 *
 * <p>Declared rather than sniffed for two reasons. It keeps the failure safe: an unknown or unreadable
 * host zone leaves a correct IST-only string instead of a wrong conversion. And it keeps rendering
 * deterministic off a real browser — a formatter that read `Intl` directly would make every assertion
 * on a rendered timestamp depend on the machine, passing on an IST laptop and failing on the UTC Linux
 * CI container. Same shape as `auth/tokenStore`: module-level state written once at startup.</p>
 */
let declaredViewerTimeZone = IST_TIME_ZONE;

/**
 * Declares the zone the viewer is actually in — called once from `main.tsx` with
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`. Silently ignores a zone this runtime cannot
 * resolve: a bad value here would otherwise corrupt every timestamp on every screen.
 */
export function setViewerTimeZone(timeZone: string | undefined): void {
  if (!timeZone) return;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    declaredViewerTimeZone = timeZone;
  } catch {
    // Keep IST. A viewer seeing no annotation is a lesser harm than one seeing a fabricated time.
  }
}

/**
 * " (9:15 AM America/New_York)" for a viewer outside India, or "" for one inside it.
 *
 * <p><b>The zone is named, not called "your time".</b> The browser's zone is a guess, and "your time"
 * asserts that the guess is right — a reader whose machine is misconfigured, or who is looking at a
 * colleague's screen or a screenshot, is told a wrong time is theirs with no way to notice. Naming the
 * identifier makes the annotation self-verifying: a reader who is not in `America/New_York` can see at a
 * glance that this line is not about them, and ignore it. It costs a few characters to turn a silent
 * error into a visible one.</p>
 *
 * <p>The IANA identifier rather than an abbreviation, because abbreviations are ambiguous in exactly the
 * way this annotation cannot afford: "IST" alone means India, Israel AND Irish Standard Time, and "CST"
 * means US Central, China and Cuba. `America/New_York` means one thing. (A picker, where the reader is
 * choosing rather than verifying, is the opposite case — see `countries.ts`, which uses human labels.)</p>
 *
 * <p>Suppressed by comparing the rendered wall clock rather than the zone name, so a viewer in
 * `Asia/Calcutta` (the legacy alias) or `Asia/Colombo` (also UTC+05:30) sees nothing added — the
 * annotation exists to resolve a discrepancy, and repeating a time back to someone whose clock already
 * reads that is pure noise.</p>
 *
 * <p><b>The date is included whenever it differs.</b> A 6:45 PM IST slot is 9:15 AM the same morning in
 * New York, but a 4:00 AM IST slot is the PREVIOUS evening there — so a bare "6:30 PM" would quietly
 * name the wrong day, which is worse than not converting at all.</p>
 */
function viewerParenthetical(date: Date, ist: IstParts, override?: string): string {
  const timeZone = override ?? declaredViewerTimeZone;

  let local: IstParts;
  try {
    local = zoneParts(date, timeZone);
  } catch {
    return ''; // an identifier this runtime cannot resolve — show the IST value plainly, never break
  }

  const sameClock =
    local.hour === ist.hour &&
    local.minute === ist.minute &&
    local.day === ist.day &&
    local.month === ist.month &&
    local.year === ist.year;
  if (sameClock) return '';

  const sameDay = local.day === ist.day && local.month === ist.month && local.year === ist.year;
  const when = sameDay
    ? clock(local)
    : `${local.day} ${MONTHS[local.month - 1]}, ${clock(local)}`;
  return ` (${when} ${timeZone})`;
}

/** e.g. "3 days ago" — used for activity feeds, last-login, notifications. */
export function formatRelativeTime(value: string | null | undefined): string {
  const date = parseApiDate(value);
  return date ? formatDistanceToNow(date, { addSuffix: true }) : '—';
}

/**
 * e.g. "14 Jul 2026" — the platform's standard short date display, on the IST calendar.
 *
 * <p>The zone matters even for a date with no time on it: an instant at 04:00 IST is the previous
 * day in UTC, so a date rendered in the wrong zone is simply the wrong date. No "IST" suffix — a
 * bare date is not something anyone has to reconcile against a clock.</p>
 */
export function formatShortDate(value: string | null | undefined): string {
  const date = parseApiDate(value);
  if (!date) return '—';
  return calendar(istParts(date));
}

/**
 * e.g. "14 Jul 2026, 6:45 PM IST" — the platform's one date-and-time display — and, for a viewer
 * outside India, "14 Jul 2026, 6:45 PM IST (9:15 AM America/New_York)".
 *
 * <p>Every timestamp a user reads goes through here, and <b>the IST portion must stay
 * character-for-character identical to the backend's `IndiaTime.Format`</b>
 * (`Willnq.Application/Common/Time/IndiaTime.cs`): a candidate reads an interview time in a
 * notification and then opens the page that renders it again.</p>
 *
 * <p>The local-time parenthetical does not break that. It is strictly <i>appended</i>, so every
 * character the email contained is still present and still first — the page adds a conversion, it does
 * not restate the time differently. The server cannot produce this part at all: it does not know the
 * recipient's zone, which is precisely why this belongs on the client and why `IndiaTime.Format` must
 * NOT grow an equivalent.</p>
 *
 * <p>The "IST" suffix is deliberate. A time with no zone on it is one the reader has to make an
 * assumption about, which should not be left to chance when an interview depends on it.</p>
 */
export function formatDateTime(
  value: string | null | undefined,
  options?: ViewerZoneOptions,
): string {
  const date = parseApiDate(value);
  if (!date) return '—';
  const parts = istParts(date);
  const local = viewerParenthetical(date, parts, options?.viewerTimeZone);
  return `${calendar(parts)}, ${clock(parts)} ${IST_SUFFIX}${local}`;
}

/**
 * e.g. "14 Jul 2026, 6:45 PM" — the date-and-time value WITHOUT the "IST" suffix, for a dense grid (the
 * Platform Users table's Last Login/Created columns) that states the zone ONCE in the column header
 * instead of repeating it on every row. Still carries the local-time parenthetical for a non-IST
 * viewer — that annotation resolves an actual discrepancy PER ROW, which a header cannot do, so it is
 * not the same repetition the "IST" suffix is here.
 */
export function formatDateTimeCompact(
  value: string | null | undefined,
  options?: ViewerZoneOptions,
): string {
  const date = parseApiDate(value);
  if (!date) return '—';
  const parts = istParts(date);
  return `${calendar(parts)}, ${clock(parts)}${viewerParenthetical(date, parts, options?.viewerTimeZone)}`;
}

/**
 * e.g. "6:45 PM IST" — time only, for compact rows that already carry their date from context
 * (a "Today's interviews" widget, say) — and "6:45 PM IST (9:15 AM America/New_York)" outside India.
 *
 * <p>Keeps the suffix, and gets the local annotation for the same reason: this is the format most
 * likely to be read at a glance and acted on immediately, so it is the last one that should make the
 * reader assume a zone. Where the viewer's date differs the annotation names it, even though the row's
 * own date comes from context — a row under "Today" reading a bare "6:30 PM" when the viewer's
 * "today" is a different day is exactly the mistake this is meant to prevent.</p>
 */
export function formatTimeOfDay(
  value: string | null | undefined,
  options?: ViewerZoneOptions,
): string {
  const date = parseApiDate(value);
  if (!date) return '—';
  const parts = istParts(date);
  return `${clock(parts)} ${IST_SUFFIX}${viewerParenthetical(date, parts, options?.viewerTimeZone)}`;
}

/**
 * e.g. "14 Jul" — day and month on the IST calendar, for dense lists where the year is noise.
 * No suffix, for the same reason `formatShortDate` has none.
 */
export function formatDayMonth(value: string | null | undefined): string {
  const date = parseApiDate(value);
  if (!date) return '—';
  const { day, month } = istParts(date);
  return `${day} ${MONTHS[month - 1]}`;
}

/** Month names in full, for a month LABEL, where the abbreviations above would read as a date. */
const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/**
 * e.g. "2026-09": the Indian calendar month an instant falls in, in the `YYYY-MM` form the backend's
 * month filters take.
 *
 * <p>The zone is the whole point: at 20:00 on 30 September in New York it is already 1 October in
 * India, and a report defaulting to the browser's month would open on September while every figure the
 * platform quotes for "this month" had already rolled over. `IndiaTime.MonthStart` on the server cuts
 * on the IST boundary, so this must name months the same way.</p>
 */
export function istMonth(date: Date = new Date()): string {
  const { year, month } = istParts(date);
  return `${year}-${pad2(month)}`;
}

/**
 * e.g. "September 2026": a `YYYY-MM` month rendered for a human. Spelled out from `MONTHS_FULL`
 * rather than via `Intl`, so the label a user reads never depends on their browser's locale while the
 * value sent to the server is a fixed `YYYY-MM`. Unparseable input is echoed back rather than shown as
 * "Invalid Date": it can only come from a hand-edited URL, and the raw text is the more useful clue.
 */
export function formatMonthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return month;
  return `${MONTHS_FULL[monthIndex]} ${match[1]}`;
}
